import { useCallback, useEffect, useRef, useState } from 'react';

import { FileUpload, type UploadedFile } from '@/components/files/FileUpload';
import { usePermissions } from '@/hooks/usePermissions';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks/useCrmObjectSnapshot';
import { useFileAssociation } from '@/lib/crm/hooks/useFileAssociation';
import { attachWithRollback } from '@/lib/files/attachWithRollback';
import { fileService, FileUploadError } from '@/lib/files/service';
import { logger } from '@/lib/logger';

import { RecordAttachmentRow } from '../attachments/RecordAttachmentRow';
import { useRecordAttachments } from '../attachments/useRecordAttachments';

interface FilesTabContentProps {
  entityId: string;
  entityTypeName: string; // resolved from the schema; any case/person-kind entity type
  orgId: number;
}

export function FilesTabContent({
  entityId,
  entityTypeName,
  orgId,
}: FilesTabContentProps) {
  // Local error state for the FileUpload → attach path. The hook owns errors
  // from its handleFile path (slideout picker); the tab's FileUpload does the
  // upload internally and only needs the attach step here.
  const [attachError, setAttachError] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { association, isLoading } = useFileAssociation(orgId, entityTypeName);

  // Read the record from the store to get its associations (kept live by WS).
  const storeObject = useCrmObjectSnapshot(entityId, orgId);

  // Hook called unconditionally — accepts CrmObject | undefined and returns
  // an empty fileEntityIds until the snapshot resolves.
  const {
    fileEntityIds,
    isUploading,
    isSubmitting,
    detachError,
    handleRemove,
    handleDownload,
  } = useRecordAttachments(storeObject, orgId, entityTypeName);

  const { hasPermission } = usePermissions();

  // Wire FileUpload's onFileUploaded to the attach step. The FileUpload
  // component handles the upload itself and delivers an already-uploaded file.
  const onFileUploaded = useCallback(
    async (uploadedFile: UploadedFile) => {
      setAttachError(null);

      if (!association || !entityId) {
        if (isMountedRef.current) {
          setAttachError('Files are not supported for this record type.');
        }
        return;
      }

      try {
        await attachWithRollback({
          orgId,
          uploadedFileId: uploadedFile.id,
          entityId,
          logComponent: 'files',
          attach: () =>
            fileService.attachToRecord(
              orgId,
              uploadedFile.id,
              entityId,
              association.forward
            ),
        });
      } catch (error) {
        logger.error(
          'Failed to attach uploaded file',
          error instanceof Error ? error : undefined,
          {
            component: 'files',
            orgId,
            entityId,
            fileId: uploadedFile.id,
            status: error instanceof FileUploadError ? error.status : undefined,
          }
        );
        if (isMountedRef.current) {
          setAttachError("We couldn't attach the file. Try again.");
        }
      }
    },
    [orgId, entityId, association]
  );

  const handleError = useCallback((message: string) => {
    setAttachError(message);
  }, []);

  if (isLoading || !association) {
    return null;
  }

  const errorMessage = attachError ?? detachError;
  const canRemove =
    hasPermission(entityTypeName, 'ahoy_association_removed') &&
    !isUploading &&
    !isSubmitting;

  return (
    <div className='flex flex-col gap-3'>
      <FileUpload
        orgId={orgId}
        onFileUploaded={onFileUploaded}
        onError={handleError}
      />
      {errorMessage && (
        <div className='text-sm text-[var(--color-text-error-primary)]'>
          {errorMessage}
        </div>
      )}
      {fileEntityIds.length === 0 ? (
        <p className='text-xs md:text-sm text-fg-secondary'>No files yet</p>
      ) : (
        <div className='flex flex-col gap-2'>
          {fileEntityIds.map(fileId => (
            <RecordAttachmentRow
              key={fileId}
              entityId={fileId}
              orgId={orgId}
              onDownload={handleDownload}
              onRemove={canRemove ? handleRemove : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
